import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Utils from "./Utils.js";

const EMPTY_OBJECT = {};
const identity = (value) => value;

const hasValues = (value) =>
  value &&
  typeof value === "object" &&
  Object.values(value).some((item) => item !== undefined && item !== "");

const mergeDefined = (...items) =>
  items.reduce((result, item) => {
    Object.entries(item || {}).forEach(([key, value]) => {
      if (value !== undefined) {
        result[key] = value;
      }
    });

    return result;
  }, {});

export default function useTableNavigationState({
  defaults = EMPTY_OBJECT,
  serialize = identity,
  deserialize = identity,
} = EMPTY_OBJECT) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const encryptedState = searchParams.get("tableState") || "";

  const tableState = useMemo(() => {
    const decodedState = Utils.getTableNavigationState(encryptedState);
    const decodedFilters = deserialize(decodedState.filters || {});

    return mergeDefined(defaults, decodedFilters);
  }, [defaults, deserialize, encryptedState]);

  const createStateParam = useCallback(
    (state = tableState) => Utils.createTableNavigationState(serialize(state)),
    [serialize, tableState],
  );

  const buildUrl = useCallback(
    (path, state = tableState) => {
      const nextState = mergeDefined(defaults, state);

      if (!encryptedState && !hasValues(nextState)) {
        return path;
      }

      const separator = path.includes("?") ? "&" : "?";
      return `${path}${separator}tableState=${encodeURIComponent(
        createStateParam(nextState),
      )}`;
    },
    [createStateParam, defaults, encryptedState, tableState],
  );

  const navigateWithTableState = useCallback(
    (path, state = tableState, options) => {
      navigate(buildUrl(path, state), options);
    },
    [buildUrl, navigate, tableState],
  );

  return {
    tableState,
    encryptedState,
    createStateParam,
    buildUrl,
    navigateWithTableState,
  };
}
